import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// Time window (ms) within which the second key of a chord must arrive.
const CHORD_TIMEOUT_MS = 800;

/**
 * Global keyboard shortcuts. Mounted once at the app root via
 * <KeyboardShortcuts /> below.
 *
 * Bindings (Sprint 11.8):
 *   g + o  → /orders
 *   g + c  → /customers
 *   g + p  → /products
 *   g + d  → /dashboard
 *   g + i  → /inventory
 *   g + s  → /settings/general
 *   /      → focus the topmost search input on the current page
 *   ?      → open the shortcuts help dialog (handled by the consumer)
 *
 * `g` opens a chord; the second key must follow within
 * CHORD_TIMEOUT_MS or the chord cancels. Shortcuts are suppressed
 * when the user is typing in an input / textarea / contenteditable.
 */
const useKeyboardShortcuts = ({ onShowHelp, onOpenPalette } = {}) => {
    const navigate = useNavigate();
    const chordOpenRef = useRef(false);
    const chordTimerRef = useRef(null);

    useEffect(() => {
        const isTypingTarget = (target) => {
            if (!target) return false;
            const tag = (target.tagName || '').toLowerCase();
            return (
                tag === 'input'
                || tag === 'textarea'
                || tag === 'select'
                || target.isContentEditable
            );
        };

        const cancelChord = () => {
            chordOpenRef.current = false;
            if (chordTimerRef.current) {
                clearTimeout(chordTimerRef.current);
                chordTimerRef.current = null;
            }
        };

        const handleKey = (e) => {
            // Cmd/Ctrl+K opens the command palette — works even while typing.
            if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                cancelChord();
                onOpenPalette?.();
                return;
            }

            // Ignore other modified keys — owned by the OS / browser.
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (isTypingTarget(e.target)) return;

            const key = e.key;

            // ?
            if (key === '?') {
                e.preventDefault();
                cancelChord();
                onShowHelp?.();
                return;
            }

            // /
            if (key === '/') {
                e.preventDefault();
                cancelChord();
                const search = document.querySelector(
                    'input[placeholder*="Search"], input[placeholder*="ابحث"], input[type="search"]'
                );
                if (search) search.focus();
                return;
            }

            // Chord step 1: 'g'
            if (key === 'g' && !chordOpenRef.current) {
                chordOpenRef.current = true;
                chordTimerRef.current = setTimeout(cancelChord, CHORD_TIMEOUT_MS);
                return;
            }

            // Chord step 2: route map
            if (chordOpenRef.current) {
                e.preventDefault();
                cancelChord();
                switch (key) {
                    case 'o': navigate('/orders');             break;
                    case 'c': navigate('/customers');          break;
                    case 'p': navigate('/products');           break;
                    case 'd': navigate('/dashboard');          break;
                    case 'i': navigate('/inventory');          break;
                    case 's': navigate('/settings/general');   break;
                    default: /* unknown chord, ignored */      break;
                }
            }
        };

        window.addEventListener('keydown', handleKey);
        return () => {
            window.removeEventListener('keydown', handleKey);
            cancelChord();
        };
    }, [navigate, onShowHelp, onOpenPalette]);
};

export default useKeyboardShortcuts;
